import * as Sentry from '@sentry/browser'
import PropTypes from 'prop-types'
import {
  branch,
  compose,
  lifecycle,
  setPropTypes,
  withHandlers,
  withStateHandlers,
  withProps,
} from 'recompose'
import { connect } from 'react-redux'
import { DirectMessage } from 'src/constants/trackingEvents'
import {
  createHomeownerMessage,
  createBuyerMessage,
} from 'src/api/mikasa/requests'
import { flashNotification } from 'src/actions'
import { injectIntl } from 'react-intl'
import { QUESTIONS, ERRORS } from 'src/constants/messages'
import { selectRouteKey } from 'src/store/selectors/routes'
import { selectClientId, selectClientIsGuest } from 'src/store/selectors/client'
import { selectCustomerProfile } from 'src/store/selectors/customerProfile'
import { selectHomeId } from 'src/store/selectors/home'
import { selectBuyerId } from 'src/store/selectors/buyerInfo'
import { dispatchEvent } from 'src/helpers/tagManager'

// Actions
import { toggleAuthModal } from 'src/actions/homeSearchAuthModal'

import QuestionSingle from './QuestionSingle'

const buyerContainer = connect(
  state => ({
    sendHandler: ({ topic, message, topicKey, metadata }) =>
      createBuyerMessage(selectBuyerId(state), {
        topic,
        topicKey,
        message,
        metadata,
      }),
    customerProfile: selectCustomerProfile(state),
    isGuest: selectClientIsGuest(state),
  }),
  dispatch => ({
    toggleSignUp: bool => dispatch(toggleAuthModal(bool)),
    notify: message =>
      dispatch(flashNotification({ message, messageType: 'error' })),
  })
)

const homeownerContainer = connect(
  state => ({
    sendHandler: ({ topic, message, topicKey }) => {
      const homeId = selectHomeId(state)
      const customerProfile = selectCustomerProfile(state)
      const clientId = selectClientId(state)

      if (!customerProfile) {
        return
      }

      return createHomeownerMessage(homeId, {
        topic,
        topicKey,
        message,
        clientId,
      })
    },
    customerProfile: selectCustomerProfile(state),
    isGuest: selectClientIsGuest(state),
  }),
  dispatch => ({
    toggleSignUp: bool => dispatch(toggleAuthModal(bool)),
    notify: message =>
      dispatch(flashNotification({ message, messageType: 'error' })),
  })
)

const propTypes = {
  topicKey: PropTypes.string,
}

export const enhance = compose(
  setPropTypes(propTypes),
  withProps(() => ({
    isBuyer: selectRouteKey() === 'buy' || selectRouteKey() === 'homeSearch',
  })),
  branch(({ isBuyer }) => isBuyer, buyerContainer, homeownerContainer),
  withStateHandlers(
    ({ question }) => ({
      inputValue: question || '',
      isLoading: false,
    }),
    {
      clearInput: () => () => ({ inputValue: '' }),
      onChange: () => e => ({ inputValue: e.target.value }),
      setLoading: () => isLoading => ({ isLoading }),
    }
  ),
  lifecycle({
    UNSAFE_componentWillUpdate(nextProps) {
      if (this.props.question !== nextProps.question) {
        this.props.onChange({ target: { value: nextProps.question } })
      }
    },
  }),
  withStateHandlers(
    { isLoading: false },
    {
      setLoading: () => value => ({ isLoading: value }),
    }
  ),
  injectIntl,
  withHandlers({
    onClick:
      ({ isGuest, toggleSignUp }) =>
      async e => {
        if (isGuest) {
          toggleSignUp(true)
        }

        return e
      },
    onSubmit:
      ({
        sendHandler,
        inputValue,
        intl,
        metadata,
        notify,
        questionTopic,
        questionTopicKey,
        setLoading,
        onSubmit,
        clearInput,
        customerProfile,
        placeholder,
        toggleSignUp,
        isGuest,
      }) =>
      async e => {
        e.preventDefault()
        if (!customerProfile) {
          notify([
            intl.formatMessage(ERRORS.defaultHeadline),
            intl.formatMessage(ERRORS.defaultMessage),
          ])
          Sentry.captureMessage(
            'Unable to send dm because customerProfile is not present'
          )
          return
        }
        setLoading(true)

        if (isGuest) {
          setTimeout(() => {
            setLoading(false)
            toggleSignUp(true)
          }, 1000)

          return
        }

        try {
          await sendHandler({
            topic: questionTopic,
            message: inputValue || placeholder,
            metadata,
            topicKey: questionTopicKey,
          })
          dispatchEvent(DirectMessage, {
            eventLabel: questionTopic,
          })
          setTimeout(() => {
            setLoading(false)
            notify(intl.formatMessage(QUESTIONS.sendSuccess))
            if (onSubmit) onSubmit(true, clearInput)
          }, 1000)
        } catch (err) {
          // handle question error

          // if network error, add more detailed error and request info to sentry report
          if (err.response != null) {
            Sentry.captureMessage('DM failed to send', {
              extra: {
                error: err.message,
                requestData: err.config.data,
                requestErrors: Object.values(err.response.data.errors),
              },
            })
          } else {
            Sentry.captureException('DM failed to send', {
              extra: {
                error: err.message,
              },
            })
          }

          setLoading(false)
          notify(intl.formatMessage(QUESTIONS.sendFailure))
          if (onSubmit) onSubmit(false, clearInput)
        }
      },
  })
)

export default enhance(QuestionSingle)
